import { media, pxToRem } from "src/styles/styleUtils";
import styled from "styled-components";
import Button, { ButtonBase } from "../components/Button";

interface SaveButtonProps {
  isError: boolean;
  isSuccess: boolean;
  needsToSave: boolean;
}
export const SaveButton = styled(Button)<SaveButtonProps>`
  transition: background-color 0.2s ease-in;
  border: ${pxToRem(2)} solid transparent;
  border-color: ${({ isError, theme }) => isError && theme.red};
  position: relative;
  flex: 0 1 ${pxToRem(119)};

  .messageArea {
    transition: opacity 0.2s ease-in;
    opacity: ${({ isError }) => (isError ? "1" : "0")};
    visibility: ${({ isError }) => (isError ? "visible" : "hidden")};
    position: absolute;
    color: ${({ isError, theme }) => (isError ? theme.red : theme.gold)};
    font-family: ${({ theme }) => theme.fontPrimary};
    font-size: ${pxToRem(14)};
    font-style: normal;
    top: calc(100% + 1rem);
    text-transform: none;
    pointer-events: none;
    cursor: default;
  }
`;

export const RoundWrapper = styled.div<{ roundId: number }>`
  position: relative;
  display: flex;
  min-width: ${pxToRem(244)};
  margin-right: ${({ roundId }) => ![0, 4].includes(roundId) && pxToRem(8)};
`;

interface ConnectorProps {
  isOpen: boolean;
  roundId: number;
  isCurrentRound: boolean;
}
export const Connector = styled.div<ConnectorProps>`
  position: relative;
  flex: 0 0 ${pxToRem(6)};
  height: calc(100%);
  margin: ${pxToRem(5)} ${pxToRem(4)} 0 0;
  color: ${({ theme, isOpen }) => (isOpen ? theme.gold : theme.grey_150)};

  border: ${pxToRem(2)} solid currentColor;
  border-right: none;
  border-color: ${({ roundId }) => roundId === 4 && "transparent"};

  &::before {
    position: absolute;
    content: "";
    height: ${pxToRem(2)};
    width: ${pxToRem(24)};
    left: ${pxToRem(-24)};
    top: ${({ roundId, isCurrentRound }) =>
      `calc(50% + ${roundId === 4 && isCurrentRound ? "6px" : roundId === 4 ? "-12px" : "2px"})`};
    background-color: currentColor;
  }
`;

export const DriverList = styled.div<{
  roundId: number;
  isCurrentRound: boolean;
}>`
  flex: 1 0 100%;
  display: flex;
  flex-direction: column;
  margin-bottom: ${({ roundId, isCurrentRound }) => roundId === 4 && !isCurrentRound && "2rem"};
  > * {
    width: 100%;
  }
`;

export const RaceList = styled.div<{ roundId: number }>`
  margin-left: ${({ roundId }) => roundId > 0 && pxToRem(10)};
  margin-bottom: ${({ roundId }) => roundId < 4 && (roundId === 0 ? pxToRem(10) : pxToRem(4))};
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.grey_150};
  font-size: ${pxToRem(10)};
  text-transform: uppercase;
  pointer-events: none;
  user-select: none;
  white-space: nowrap;
`;

export const SeparatorSpan = styled.span`
  display: inline-block;
  margin: 0 ${pxToRem(7)};
  height: 100%;
  width: ${pxToRem(1)};
  background-color: white;
  &:last-child {
    display: none;
  }

  ${media.tablet} {
    height: 1rem;
  }
`;
export const ControlBar = styled.div`
  margin-bottom: ${pxToRem(4)};
  color: ${({ theme }) => theme.grey_150};
  font-size: ${pxToRem(10)};
  font-weight: bold;
`;

export const LockMessage = styled.div<{ isOpen?: boolean }>`
  color: ${({ theme, isOpen }) => isOpen && theme.gold};
  svg {
    margin-right: 4px;
  }
`;

export const ResultContainer = styled.div`
  display: flex;
  justify-content: space-between;
`;

export const ScoringButton = styled.div`
  ${ButtonBase}
  text-transform: none;
  margin-right: ${pxToRem(33)};
`;

export const RoundFooter = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: ${pxToRem(24)};
  height: ${pxToRem(132)};

  .round-save {
    display: flex;
    > svg {
      height: ${pxToRem(44)};
      margin-right: 0.5rem;
      ${media.tablet} {
        display: none;
      }
    }
  }

  .social-share {
    display: flex;
    flex-direction: column;
    align-items: center;
    font-family: ${({ theme }) => theme.fontSecondary};
    font-size: ${pxToRem(20)};
    color: #fff;
    p {
      margin-bottom: ${pxToRem(4)};
    }
    div {
      width: 100%;
      display: flex;
      justify-content: center;
      gap: ${pxToRem(10)};
      a {
        color: black;
        background: #fff;
        border-radius: ${pxToRem(4)};
        width: ${pxToRem(32)};
        height: ${pxToRem(32)};
        display: flex;
        justify-content: center;
        align-items: center;
        svg {
          height: ${pxToRem(17)};
          fill: #000;
          path {
            fill: #000;
          }
        }
        &:hover {
          cursor: pointer;
        }
      }
    }
  }
`;

export const NCSLogoArea = styled.div`
  position: absolute;
  top: calc(100% + ${pxToRem(96)});
  display: flex;
  justify-content: center;
  width: 100%;
  user-select: none;
  pointer-events: none;

  svg {
    height: ${pxToRem(67)};
  }

  ${media.tablet} {
    top: calc(100% + ${pxToRem(80)});
  }
`;
export const TrophyArea = styled.div`
  position: absolute;
  bottom: calc(100% - ${pxToRem(-20)});
  display: flex;
  justify-content: center;
  width: 100%;

  svg {
    height: ${pxToRem(138)};

    ${media.tablet} {
      height: ${pxToRem(72)};
      position: relative;
      left: -0.5rem;
    }
  }
`;

interface ColContainerProps {
  isCollapsed: boolean;
  roundId: number;
}
export const ColContainer = styled.div<ColContainerProps>`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  color: white;
  font-size: 10px;

  &:first-child ${Connector} {
    display: none; /* hide the connector for r1 and left col on mobile */
  }

  &:last-child {
    bottom: 20px;

    ${media.tablet} {
      bottom: 5px;
    }
  }

  &:last-child ${RoundFooter} {
    height: 82px;
    .social-share {
      position: relative;
      top: ${pxToRem(90)};
      ${media.tablet} {
        top: ${pxToRem(60)};
      }
    }
  }

  ${media.tablet} {
    justify-content: flex-start;
    margin-bottom: ${pxToRem(8)};

    &:nth-of-type(2) {
      margin-top: ${({ roundId }) => (roundId === 4 ? pxToRem(68) : pxToRem(36))};
      margin-bottom: ${({ roundId }) => roundId === 4 && pxToRem(112)};
    }

    ${RoundWrapper} {
      min-width: ${({ isCollapsed }) => isCollapsed && "unset"};
    }

    ${RaceList} {
      svg {
        margin-right: ${({ isCollapsed }) => !isCollapsed && pxToRem(10)};
      }
      margin-bottom: ${pxToRem(24)};
    }

    ${ControlBar} {
      display: ${({ isCollapsed }) => isCollapsed && "none"};
    }

    ${RoundFooter} {
      display: ${({ isCollapsed }) => isCollapsed && "none"};
    }
  }
`;
